import { useState, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useCompanyUsers } from "@/hooks/useCompanyUsers";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Users } from "lucide-react";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-700 dark:text-green-400",
  invited: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  deactivated: "bg-red-500/10 text-red-700 dark:text-red-400",
};

const Team = () => {
  const navigate = useTabNavigate();
  const { data: users, isLoading } = useCompanyUsers();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const roles = useMemo(() => {
    if (!users) return [];
    const set = new Map<string, string>();
    users.forEach((u) => {
      if (u.roles?.name) set.set(u.role_id, u.roles.name);
    });
    return Array.from(set, ([id, name]) => ({ id, name }));
  }, [users]);

  const filtered = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.title || "").toLowerCase().includes(q);
      const matchesRole = roleFilter === "all" || u.role_id === roleFilter;
      const matchesStatus = statusFilter === "all" || u.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const initials = (u: any) =>
    `${(u.first_name || "")[0] || ""}${(u.last_name || "")[0] || ""}`.toUpperCase() || "?";

  if (isLoading) return <p className="text-muted-foreground p-4">Loading team…</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" /> Team
          </h1>
          <p className="text-sm text-muted-foreground">{users?.length ?? 0} members</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name, email, title…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="deactivated">Deactivated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((u) => (
          <Card key={u.id} className="cursor-pointer active:bg-muted/50" onClick={() => navigate(`/controller/team/${u.user_id}`)}>
            <CardContent className="p-4 flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {u.profile_photo_url && <AvatarImage src={u.profile_photo_url} />}
                <AvatarFallback className="text-xs">{initials(u)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{u.first_name} {u.last_name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.title || u.email}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {u.roles?.name && (
                  <Badge variant="outline" className="text-[10px]" style={u.roles.color ? { borderColor: u.roles.color, color: u.roles.color } : {}}>
                    {u.roles.name}
                  </Badge>
                )}
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_COLORS[u.status] || "text-muted-foreground"}`}>
                  {u.status}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
        {!filtered.length && <p className="text-center text-muted-foreground py-8">No team members found.</p>}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/controller/team/${u.user_id}`)}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      {u.profile_photo_url && <AvatarImage src={u.profile_photo_url} />}
                      <AvatarFallback className="text-xs">{initials(u)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{u.first_name} {u.last_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm">{u.title || "—"}</TableCell>
                <TableCell>
                  {u.roles?.name && (
                    <Badge variant="outline" className="text-[10px]" style={u.roles.color ? { borderColor: u.roles.color, color: u.roles.color } : {}}>
                      {u.roles.name}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLORS[u.status] || "text-muted-foreground"}`}>
                    {u.status}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {u.last_login ? format(new Date(u.last_login), "MMM d, yyyy") : "Never"}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No team members found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Team;
